import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { data as fData, dataHora, numero, plural } from '../formato';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import {
  Area, Aviso, Campo, Carregando, Dialogo, ErroDaApi, LinkProtegido, Rn, Selo,
  Vazio,
} from '../ui';
import type { Tom } from '../ui';
import {
  FormCurva, FormRetirarCurva, ResumoDoAjuste, ajusteDaCurva,
} from './CurvaDoPicnometro';
import type { CurvaDoCertificado } from './CurvaDoPicnometro';

/**
 * Os «triviais» — a aferição interna que o §14.2 pede.
 *
 * ── Por que esta tela existe ───────────────────────────────────────────
 *
 * O banco tinha a aferição interna inteira desde a `0021`: certificado,
 * padrões que sustentam a cadeia, objeto aferido, item, leituras por trás
 * da média, critério por norma com o veredito calculado, e o valor
 * vigente com vigência sem sobreposição. Não havia UMA rota de escrita.
 *
 * O efeito, medido em 31/08/2026: 28 cilindros CBR, 32 anéis de
 * cisalhamento, 6 anéis de adensamento e 2 cilindros Proctor, todos
 * aferidos em maio e com certificado em papel, e nenhum deles conseguia
 * entrar. Sem eles `equipamento_valor` fica vazio — e o volume do
 * cilindro é o divisor da massa específica de toda compactação feita com
 * ele. A conta que não acha a constante é a conta que alguém completa de
 * cabeça.
 *
 * ── O que esta tela NÃO faz ────────────────────────────────────────────
 *
 * Não digita `conforme`: isso é gatilho desde a `0021`, comparando
 * leitura com critério. Foi um «Sim» digitado à mão que deixou o cilindro
 * 21 passar 0,015 mm abaixo do mínimo (`D-21`).
 *
 * E não publica valor: quem publica é o banco, ao aprovar (`0094`).
 * Aprovar é o gesto único — quem aprova assume os números, e os números
 * que entram são exatamente os do papel.
 */

interface Certificado {
  id: string;
  codigo: string;
  revisao: number;
  titulo: string;
  categoria: string;
  data_afericao: string;
  procedimento: string;
  condicoes_ambientais: string | null;
  responsavel: string | null;
  verificador: string | null;
  aprovador: string | null;
  data_aprovacao: string | null;
  proxima_afericao: string | null;
  /** O que o banco vai gravar ao aprovar, quando a data não foi informada
   *  — derivada da periodicidade dos objetos (0158). */
  proxima_sugerida?: string | null;
  status: string;
  objetos: number;
  objetos_nao_conformes: number;
  padroes: number;
  /** RN-201: padrões sem certificado que cubra o dia da aferição. */
  padroes_aguardando?: number;
  valores_publicados: number;
  notas: string[] | null;
  observacoes: string | null;
}

interface Padrao {
  id: string; ordem: number; descricao: string;
  numero_certificado: string | null;
  codigo_patrimonial: string | null; equipamento: string | null;
  /** RN-201 (0199). */
  coberto?: boolean | null;
  ultimo_certificado?: string | null;
  ultimo_valido_ate?: string | null;
}

interface Leitura { ordem: number; valor: number }

interface Criterio {
  id: string; ordem: number; norma_texto: string; nao_informa: boolean;
  valor_nominal: number | null; tolerancia: number | null;
  minimo: number | null; maximo: number | null; conforme: boolean | null;
}

interface Item {
  id: string; verificacao_id: string; ordem: number; item: string;
  simbolo: string | null; grandeza: string | null;
  valor_medido: number | null; unidade: string | null;
  derivado: boolean; conforme: boolean | null; observacao: string | null;
  leituras: Leitura[] | null; criterios: Criterio[] | null;
}

interface Objeto {
  id: string; equipamento_id: string; ordem_no_certificado: number;
  conforme: boolean | null; codigo_patrimonial: string; equipamento: string;
  categoria: string; observacoes: string | null;
  uso_autorizado_em: string | null; uso_justificativa: string | null;
  itens: Item[];
}

interface Valor {
  id: string; equipamento_id: string; codigo_patrimonial: string;
  grandeza: string; valor: number; unidade: string;
  vigencia_inicio: string; vigencia_fim: string | null;
}

/**
 * O que a aprovação vai publicar (`0105`).
 *
 * Vem da MESMA função que a publicação percorre — não é uma simulação
 * escrita à parte, que um dia mostraria uma coisa e gravaria outra.
 */
interface Previa {
  equipamento_id: string; codigo_patrimonial: string; equipamento: string;
  grandeza: string; valor: number; unidade: string;
  derivado: boolean; observacoes: string | null;
  valor_atual: number | null; unidade_atual: string | null;
}

interface Ficha {
  certificado: Certificado; padroes: Padrao[];
  objetos: Objeto[]; valores: Valor[]; previa?: Previa[];
  /** RN-259: a curva de massa por temperatura do picnômetro, com o ajuste do banco. */
  curvas?: CurvaDoCertificado[];
  /** RN-234 (0243): na homologação, quem calibrou pode aprovar o próprio. */
  aprovacaoPropria?: boolean;
}

const TOM: Record<string, Tom> = {
  aprovado: 'ok', aguardando_aprovacao: 'andamento',
  rascunho: 'neutro', cancelado: 'critico',
};

const ROTULO: Record<string, string> = {
  aprovado: 'aprovado', aguardando_aprovacao: 'aguardando aprovação',
  rascunho: 'rascunho', cancelado: 'cancelado',
};

/** Número com as casas que a grandeza merece, sem zeros de enfeite. */
const g = (v: unknown, unidade?: string | null, casas = 4) =>
  v === null || v === undefined
    ? '—'
    : `${numero(v, casas, true)}${unidade ? ` ${unidade}` : ''}`;

/** A faixa que o critério exige, escrita como a norma escreve. */
function faixa(c: Criterio): string {
  if (c.nao_informa) return 'a norma não trata';
  if (c.valor_nominal !== null && c.tolerancia !== null) {
    return `${numero(c.valor_nominal, 4, true)} ± ${numero(c.tolerancia, 4, true)}`;
  }
  if (c.minimo !== null && c.maximo !== null) {
    return `${numero(c.minimo, 4, true)} a ${numero(c.maximo, 4, true)}`;
  }
  if (c.minimo !== null) return `≥ ${numero(c.minimo, 4, true)}`;
  if (c.maximo !== null) return `≤ ${numero(c.maximo, 4, true)}`;
  if (c.valor_nominal !== null) return numero(c.valor_nominal, 4, true);
  return '—';
}

// ── A lista ────────────────────────────────────────────────────────────

export function VerificacoesTela() {
  const { pode } = useSessao();
  const [lista, setLista] = useState<Certificado[] | null>(null);
  const [novo, setNovo] = useState(false);
  const [semValor, setSemValor] = useState<
    { equipamento_id: string; codigo_patrimonial: string;
      nome: string; categoria: string }[]>([]);
  const [erro, setErro] = useState<unknown>(null);

  const carregar = useCallback(() => {
    api<Certificado[]>('/verificacoes/certificados')
      .then(setLista).catch(setErro);
    api<typeof semValor>('/verificacoes/sem-valor')
      .then(setSemValor).catch(() => setSemValor([]));
  }, []);

  useEffect(carregar, [carregar]);

  if (erro && !lista) return <ErroDaApi erro={erro} />;
  if (!lista) return <Carregando texto="Carregando os certificados" />;

  return (
    <div className="pagina">
      <div className="pagina-cabeca">
        <div className="linha-topo">
          <h1 className="cresce">Calibração interna</h1>
          {pode('equipamento.verificar') && (
            <>
              {/*
                * A campanha de massa tem porta própria porque é o gesto
                * mais repetido da metrologia da casa — «aferir a massa de
                * todas as cápsulas» — e pela porta genérica ele custa uma
                * requisição por objeto, cada uma escolhendo numa lista de
                * duzentos itens.
                */}
              <Link className="botao secundario" to="/equipamentos/aferir-massas">
                Calibração interna em lote
              </Link>
              <button
                type="button" className="botao"
                onClick={() => setNovo(true)}
              >
                + Certificado
              </button>
            </>
          )}
        </div>
        <p className="sub">
          Os «triviais» da casa. Um certificado cobre muitos objetos porque
          foi assim que a campanha aconteceu — uma data, um responsável, um
          jogo de padrões, 28 cilindros. Aprovar é o que <b>publica</b> os
          valores que o cálculo do ensaio vai consultar.
        </p>
      </div>

      {semValor.length > 0 && (
        <Aviso tipo="atencao" titulo={`${semValor.length} sem constante vigente`}>
          Cápsula sem tara, cilindro sem volume, anel sem altura — o cálculo
          vai procurar e não achar.
          <div className="rola" style={{ marginTop: 8 }}>
            <table className="lista compacta">
              <thead><tr><th>Código</th><th>Equipamento</th><th>Categoria</th></tr></thead>
              <tbody>
                {semValor.slice(0, 12).map((e) => (
                  <tr key={e.equipamento_id}>
                    <td className="mono">{e.codigo_patrimonial}</td>
                    <td><Link to={`/equipamento/${e.equipamento_id}`}>{e.nome}</Link></td>
                    <td>{e.categoria}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {semValor.length > 12 && (
              <p className="sub">… e mais {semValor.length - 12}.</p>
            )}
          </div>
        </Aviso>
      )}

      <div className="cartao">
        {lista.length === 0
          ? <Vazio>Nenhum certificado interno registrado ainda.</Vazio>
          : (
            <div className="rola">
              <table className="lista">
                <thead>
                  <tr>
                    <th>Certificado</th><th>Título</th><th>Calibração interna</th>
                    <th className="num">Objetos</th>
                    <th className="num">Não conf.</th>
                    <th className="num">Padrões</th>
                    <th className="num">Valores</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">
                        <Link to={`/equipamentos/verificacoes/${c.id}`}>
                          {c.codigo}
                        </Link>
                        {c.revisao > 0 && <span className="sub"> R{c.revisao}</span>}
                      </td>
                      <td>{c.titulo}</td>
                      <td>{fData(c.data_afericao)}</td>
                      <td className="num">{c.objetos}</td>
                      <td className="num">
                        {Number(c.objetos_nao_conformes) > 0
                          ? <b className="ruim">{c.objetos_nao_conformes}</b>
                          : <span className="nada">—</span>}
                      </td>
                      <td className="num">{c.padroes}</td>
                      <td className="num">
                        {Number(c.valores_publicados) > 0
                          ? c.valores_publicados
                          : <span className="nada">—</span>}
                      </td>
                      <td>
                        <Selo tom={TOM[c.status] ?? 'neutro'}>
                          {ROTULO[c.status] ?? c.status}
                        </Selo>
                        {c.status !== 'aprovado' && c.status !== 'cancelado'
                          && Number(c.padroes_aguardando ?? 0) > 0 && (
                          <>
                            {' '}
                            <Selo tom="atencao" titulo="RN-201: falta o certificado do padrão que cubra a data da calibração interna">
                              Aguarda cert. do padrão
                            </Selo>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {novo && (
        <FormCertificado aoFechar={() => { setNovo(false); carregar(); }} />
      )}
    </div>
  );
}

// ── A ficha de um certificado ──────────────────────────────────────────

export function VerificacaoTela() {
  const { id } = useParams<{ id: string }>();
  const { pode } = useSessao();
  const [f, setF] = useState<Ficha | null>(null);
  useCaminho(f ? [
    { rotulo: 'Calibração interna', para: '/equipamentos/verificacoes' },
    { rotulo: f.certificado.titulo, codigo: f.certificado.codigo },
  ] : null);
  const [erro, setErro] = useState<unknown>(null);
  const [form, setForm] = useState<
    { que: 'aprovar' } | { que: 'padrao' }
    | { que: 'autorizar'; objeto: Objeto }
    | { que: 'curva'; curva?: CurvaDoCertificado }
    | { que: 'retirar'; curva: CurvaDoCertificado } | null>(null);

  const carregar = useCallback(() => {
    api<Ficha>(`/verificacoes/certificados/${id}`)
      .then((r) => { setF(r); setErro(null); })
      .catch(setErro);
  }, [id]);

  useEffect(carregar, [carregar]);

  if (erro && !f) return <ErroDaApi erro={erro} />;
  if (!f) return <Carregando texto="Carregando o certificado" />;

  const c = f.certificado;
  const aprovado = c.status === 'aprovado';
  const podeAprovar = pode('equipamento.aprovar_certificado');
  /* RN-201 (onda 210): padrão sem certificado que cubra o dia. */
  const aguardando = aprovado || c.status === 'cancelado'
    ? [] : f.padroes.filter((p) => p.coberto === false);

  return (
    <div className="pagina">
      <div className="pagina-cabeca">
        <h1>
          {c.codigo} · {c.titulo}
          {' '}<Selo tom={TOM[c.status] ?? 'neutro'}>{ROTULO[c.status] ?? c.status}</Selo>
        </h1>
        {/*
          * O papel — «com todas as premissas da 17025, citando os padrões
          * utilizados» (Humberto, 31/08/2026).
          *
          * O rascunho também sai, com marca d'água de preliminar:
          * conferir na tela é pior do que conferir no documento, e a
          * marca impede que o preliminar seja arquivado como válido.
          */}
        <div className="acoes">
          <LinkProtegido
            className="botao secundario"
            caminho={`/verificacoes/certificados/${id}/documento`}
            nomeQueda={`certificado-${c.codigo}.html`}
          >
            Ver o certificado
          </LinkProtegido>
          <LinkProtegido
            className="botao secundario"
            caminho={`/verificacoes/certificados/${id}/documento?pdf=1`}
            nomeQueda={`certificado-${c.codigo}.pdf`}
          >
            Baixar o PDF
          </LinkProtegido>
        </div>
      </div>

      {/* ── 1. O cabeçalho do papel ──────────────────────────────── */}

      <div className="cartao">
        <h2>A campanha</h2>
        <div className="rola">
          <table className="dados">
            <tbody>
              <tr><th>Data da calibração interna</th><td>{fData(c.data_afericao)}</td></tr>
              <tr><th>Procedimento</th><td>{c.procedimento}</td></tr>
              <tr>
                <th>Condições ambientais</th>
                <td>{c.condicoes_ambientais ?? <span className="nada">—</span>}</td>
              </tr>
              <tr>
                <th>Calibrado por</th>
                <td>{c.responsavel ?? <span className="nada">—</span>}</td>
              </tr>
              <tr>
                <th>Aprovado por</th>
                <td>
                  {c.aprovador
                    ? <>{c.aprovador} <span className="sub">
                      {c.data_aprovacao ? dataHora(c.data_aprovacao) : ''}</span></>
                    : <span className="nada">ainda não aprovado</span>}
                </td>
              </tr>
              <tr>
                <th>Próxima calibração interna</th>
                <td>{c.proxima_afericao
                  ? fData(c.proxima_afericao)
                  : c.proxima_sugerida
                    ? <>{fData(c.proxima_sugerida)} <span className="sub">
                      pela periodicidade dos objetos — grava ao aprovar</span></>
                    : aprovado
                      ? <span className="nada">não definida</span>
                      : <span className="nada">não definida — quem aprovar
                        vai informar</span>}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {aguardando.length > 0 && (
          <Aviso tipo="atencao" titulo="Aguardando o certificado do padrão">
            {aguardando.map((p) => `${p.codigo_patrimonial ?? p.descricao}${
              p.ultimo_certificado
                ? ` (último certificado ${p.ultimo_certificado}${p.ultimo_valido_ate
                  ? `, válido até ${fData(p.ultimo_valido_ate)}` : ''})`
                : ' (nenhum certificado cadastrado)'}`).join('; ')}
            {`. A calibração interna de ${fData(c.data_afericao)} fica gravada; para aprovar, `}
            cadastre na ficha do padrão a calibração que cubra esse dia — o
            certificado se liga sozinho e a aprovação fica liberada.
            {' '}
            <Rn codigo="RN-201"
              diz="A calibração interna grava com o padrão vencido na plataforma, mas só é aprovada quando existir, para cada padrão, calibração externa não reprovada que cubra a data da calibração interna." />
          </Aviso>
        )}
        {!aprovado && podeAprovar && (
          <div className="acoes">
            <button
              type="button" className="botao"
              disabled={aguardando.length > 0}
              title={aguardando.length > 0
                ? 'Falta o certificado do padrão que cubra a data da calibração interna (RN-201)'
                : undefined}
              onClick={() => setForm({ que: 'aprovar' })}
            >
              Aprovar e publicar os valores
            </button>
          </div>
        )}
        {!aprovado && !podeAprovar && (
          <p className="sub">
            Você está vendo, não aprovando. Quem afere não aprova o próprio
            trabalho (§15).
          </p>
        )}
      </div>

      {/* ── 2. Os padrões que sustentam a cadeia ─────────────────── */}

      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Rastreabilidade dos padrões</h2>
          {!aprovado && pode('equipamento.verificar') && (
            <button
              type="button" className="botao discreto"
              onClick={() => setForm({ que: 'padrao' })}
            >
              + Padrão
            </button>
          )}
        </div>
        <p className="sub">
          O volume do cilindro vale porque o paquímetro que o mediu tinha
          certificado válido naquele dia. Sem esta lista, «calibrado
          internamente» é só uma frase.
        </p>
        {f.padroes.length === 0
          ? (
            <Aviso tipo="atencao" titulo="Nenhum padrão registrado">
              A cadeia de rastreabilidade deste certificado não tem lastro.
            </Aviso>
          )
          : (
            <div className="rola">
              <table className="lista">
                <thead>
                  <tr><th>#</th><th>Padrão</th><th>Certificado do padrão</th></tr>
                </thead>
                <tbody>
                  {f.padroes.map((p) => (
                    <tr key={p.id}>
                      <td className="num">{p.ordem}</td>
                      <td>
                        {p.equipamento
                          ? <Link to={`/equipamento/${p.codigo_patrimonial}`}>
                            {p.descricao}
                          </Link>
                          : p.descricao}
                      </td>
                      <td className="mono">
                        {p.numero_certificado ?? <span className="nada">—</span>}
                        {p.coberto === false && !aprovado && (
                          <>
                            {' '}
                            <Selo tom="atencao" titulo="Não cobre a data da calibração interna (RN-201)">
                              Não cobre o dia
                            </Selo>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* ── 3. Os objetos aferidos ───────────────────────────────── */}

      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Objetos calibrados</h2>
          <span className="sub">{f.objetos.length}</span>
        </div>

        {f.objetos.map((o) => (
          <div key={o.id} className="bloco">
            <div className="linha-topo">
              <b className="cresce">
                <Link to={`/equipamento/${o.equipamento_id}`}>
                  {o.codigo_patrimonial} · {o.equipamento}
                </Link>
              </b>
              {o.conforme === false
                ? <Selo tom="critico">Não conforme</Selo>
                : o.conforme === true
                  ? <Selo tom="ok">Conforme</Selo>
                  : <Selo tom="neutro">Sem critério</Selo>}
            </div>

            {o.conforme === false && (
              o.uso_autorizado_em
                ? (
                  <Aviso tipo="atencao" titulo="Uso autorizado mesmo assim">
                    {o.uso_justificativa}
                    <span className="sub"> · {dataHora(o.uso_autorizado_em)}</span>
                  </Aviso>
                )
                : (
                  <Aviso tipo="erro" titulo="Fora de tolerância">
                    Não publica valor e não deve entrar em ensaio. Se a
                    Testesolo decidir usá-lo assim mesmo, a decisão precisa
                    ficar escrita.
                    {podeAprovar && !aprovado && (
                      <div className="acoes">
                        <button
                          type="button" className="botao discreto"
                          onClick={() => setForm({ que: 'autorizar', objeto: o })}
                        >
                          Autorizar o uso
                        </button>
                      </div>
                    )}
                  </Aviso>
                )
            )}

            <div className="rola">
              <table className="lista compacta">
                <thead>
                  <tr>
                    <th>Item</th><th className="num">Medido</th>
                    <th>Leituras</th><th>Critério</th><th>Conforme</th>
                  </tr>
                </thead>
                <tbody>
                  {o.itens.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {i.simbolo && <span className="mono">{i.simbolo} </span>}
                        {i.item}
                        {i.derivado && <span className="sub"> derivado</span>}
                      </td>
                      <td className="num">{g(i.valor_medido, i.unidade)}</td>
                      <td className="sub mono">
                        {i.leituras?.length
                          ? i.leituras.map((l) => numero(l.valor, 4, true)).join(' · ')
                          : <span className="nada">—</span>}
                      </td>
                      <td className="sub">
                        {i.criterios?.length
                          ? i.criterios.map((cr) => (
                            <div key={cr.id}>
                              {cr.norma_texto}: {faixa(cr)}
                            </div>
                          ))
                          : <span className="nada">nenhuma norma limita</span>}
                      </td>
                      <td>
                        {i.conforme === null
                          ? <span className="nada">—</span>
                          : i.conforme
                            ? <Selo tom="ok">Sim</Selo>
                            : <Selo tom="critico">Não</Selo>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* ── 3a. As curvas de calibração (RN-259) ────────────────── */}

      {(!aprovado && c.status !== 'cancelado') || (f.curvas ?? []).length > 0 ? (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Curvas de calibração</h2>
            {!aprovado && c.status !== 'cancelado' && pode('equipamento.verificar') && (
              <button
                type="button" className="botao discreto"
                onClick={() => setForm({ que: 'curva' })}
              >
                ＋ Curva de picnômetro
              </button>
            )}
          </div>
          <p className="sub">
            A massa do picnômetro cheio de água em cada temperatura. Ao aprovar,
            o sistema publica a parábola ajustada a estes pontos — dentro da
            faixa medida, nunca fora dela. O polinômio impresso no papel fica só
            como registro.
          </p>
          {(f.curvas ?? []).length === 0
            ? <Vazio>Nenhuma curva neste certificado.</Vazio>
            : (f.curvas ?? []).map((cv) => {
              const aj = ajusteDaCurva(cv);
              return (
                <div key={cv.id} className="bloco">
                  <div className="linha-topo">
                    <b className="cresce">
                      <Link to={`/equipamento/${cv.equipamento_id}`}>
                        {cv.codigo_patrimonial}
                      </Link>
                      {' · '}{cv.descricao}
                    </b>
                    <span className="sub">
                      {plural(cv.pontos.length, 'ponto', 'pontos')}
                    </span>
                    {cv.grandeza === 'massa_com_agua' && !aprovado
                      && c.status !== 'cancelado'
                      && pode('equipamento.verificar') && (
                      <>
                        <button
                          type="button" className="botao discreto pequeno"
                          onClick={() => setForm({ que: 'curva', curva: cv })}
                        >
                          Editar
                        </button>
                        <button
                          type="button" className="botao discreto pequeno"
                          onClick={() => setForm({ que: 'retirar', curva: cv })}
                        >
                          Retirar
                        </button>
                      </>
                    )}
                  </div>
                  {aj
                    ? <ResumoDoAjuste {...aj} />
                    : cv.grandeza !== 'massa_com_agua' ? null : (
                      <Aviso tipo="atencao" titulo="Sem ajuste">
                        Com menos de 4 pontos a parábola não se ajusta, e a curva
                        não vale para M₁ na massa específica.
                      </Aviso>
                    )}
                  {cv.funcao_texto && (
                    <p className="sub">
                      Polinômio do certificado (só registro): <span className="mono">
                        {cv.funcao_texto}</span>
                    </p>
                  )}
                </div>
              );
            })}
        </div>
      ) : null}

      {/* ── 3b. O que a aprovação VAI publicar (0105) ────────────── */}

      {!aprovado && (f.previa?.length ?? 0) > 0 && (
        <div className="cartao">
          <h2>O que a aprovação vai publicar</h2>
          <p className="sub">
            {f.previa!.length} constante(s). A partir da assinatura, o cálculo
            de todo ensaio feito com estes equipamentos passa a usar estes
            números — e quem assina está afirmando que eles valem.
          </p>

          <div className="rola">
            <table className="lista">
              <thead>
                <tr>
                  <th>Equipamento</th><th>Grandeza</th>
                  <th className="num">Passa a valer</th>
                  <th className="num">Valia</th>
                </tr>
              </thead>
              <tbody>
                {f.previa!.map((p) => (
                  <tr key={`${p.equipamento_id}-${p.grandeza}`}>
                    <td className="mono">
                      <Link to={`/equipamento/${p.equipamento_id}`}>
                        {p.codigo_patrimonial}
                      </Link>
                    </td>
                    <td>
                      {p.grandeza}
                      {/*
                        * Derivado não pode parecer medido. O volume do
                        * anel sai de π·d²/4·h com o diâmetro e a altura
                        * do próprio certificado (`0104`) — quem assina
                        * tem direito de ver a diferença sem procurar.
                        */}
                      {p.derivado && (
                        <div className="sub">derivado · {p.observacoes}</div>
                      )}
                    </td>
                    <td className="num">{g(p.valor, p.unidade, 6)}</td>
                    <td className="num">
                      {p.valor_atual !== null
                        ? g(p.valor_atual, p.unidade_atual ?? p.unidade, 6)
                        : <span className="sub">nada ainda</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 4. O que o certificado publicou ──────────────────────── */}

      <div className="cartao">
        <h2>Valores publicados</h2>
        <p className="sub">
          São estes que o cálculo do ensaio consulta. Recalibrar muda o valor
          de hoje em diante, nunca o de ontem.
        </p>
        {f.valores.length === 0
          ? (
            <Vazio>
              {aprovado
                ? 'Este certificado não publicou nenhuma constante.'
                : 'Nada publicado ainda — aprovar é o que publica.'}
            </Vazio>
          )
          : (
            <div className="rola">
              <table className="lista">
                <thead>
                  <tr>
                    <th>Equipamento</th><th>Grandeza</th>
                    <th className="num">Valor</th><th>Desde</th><th>Até</th>
                  </tr>
                </thead>
                <tbody>
                  {f.valores.map((v) => (
                    <tr key={v.id}>
                      <td className="mono">
                        <Link to={`/equipamento/${v.equipamento_id}`}>
                          {v.codigo_patrimonial}
                        </Link>
                      </td>
                      <td>{v.grandeza}</td>
                      <td className="num">{g(v.valor, v.unidade, 6)}</td>
                      <td>{fData(v.vigencia_inicio)}</td>
                      <td>
                        {v.vigencia_fim
                          ? fData(v.vigencia_fim)
                          : <span className="sub">vigente</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {form?.que === 'padrao' && (
        <FormPadrao
          certificado={c}
          proximaOrdem={f.padroes.length + 1}
          aoFechar={() => { setForm(null); carregar(); }}
        />
      )}
      {form?.que === 'aprovar' && (
        <FormAprovar
          certificado={c}
          previa={f.previa ?? []}
          aprovacaoPropria={f.aprovacaoPropria === true}
          aoFechar={() => { setForm(null); carregar(); }}
        />
      )}
      {form?.que === 'curva' && (
        <FormCurva
          certificadoId={c.id}
          curva={form.curva}
          aoFechar={() => { setForm(null); carregar(); }}
        />
      )}
      {form?.que === 'retirar' && (
        <FormRetirarCurva
          certificadoId={c.id}
          curva={form.curva}
          aoFechar={() => { setForm(null); carregar(); }}
        />
      )}
      {form?.que === 'autorizar' && (
        <FormAutorizarUso
          objeto={form.objeto}
          aoFechar={() => { setForm(null); carregar(); }}
        />
      )}
    </div>
  );
}

// ── Os dois diálogos ───────────────────────────────────────────────────

function FormAprovar({
  certificado, previa, aprovacaoPropria, aoFechar,
}: {
  certificado: Certificado; previa: Previa[]; aprovacaoPropria: boolean;
  aoFechar(): void;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);
  /*
   * A próxima aferição (UX-035). O certificado pode já tê-la; senão o
   * banco deriva da periodicidade dos objetos; senão, é aqui que quem
   * aprova diz — e sem dizer, o botão não vai.
   */
  const precisaData = !certificado.proxima_afericao && !certificado.proxima_sugerida;
  const [proxima, setProxima] = useState('');
  const dataInvalida = precisaData
    && (!proxima || proxima <= certificado.data_afericao.slice(0, 10));

  const enviar = async () => {
    setIndo(true);
    try {
      await api(`/verificacoes/certificados/${certificado.id}/aprovar`,
        { corpo: { confirmo: true, ...(precisaData ? { proximaAfericao: proxima } : {}) } });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };

  return (
    <Dialogo aberto titulo="Aprovar o certificado" aoFechar={aoFechar}>
      <p>
        Aprovar publica <b>{previa.length} constante(s)</b> de{' '}
        {certificado.objetos} objeto(s). A partir daí, o cálculo de todo ensaio
        feito com esses equipamentos passa a usar estes números.
      </p>
      {/*
        * Quem assina afirma que os números valem. Afirmar sem ver é a
        * `D-21` do outro lado: lá foi um «Sim» digitado sem olhar.
        */}
      {previa.length > 0 && (
        <div className="rola" style={{ maxHeight: '14rem' }}>
          <table className="lista">
            <tbody>
              {previa.map((p) => (
                <tr key={`${p.equipamento_id}-${p.grandeza}`}>
                  <td className="mono">{p.codigo_patrimonial}</td>
                  <td>
                    {p.grandeza}
                    {p.derivado && <span className="sub"> · derivado</span>}
                  </td>
                  <td className="num">{g(p.valor, p.unidade, 6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {aprovacaoPropria ? (
        <p className="sub" data-teste="aprovacao-propria">
          Aprovado não volta atrás: uma correção exige certificado novo, com
          nova numeração. <b>Homologação:</b> quem calibrou pode aprovar o
          próprio certificado, e ele sai com a nota «aprovado por quem executou
          a calibração interna» (<Rn codigo="RN-234" diz="Na homologação, quem calibrou
          aprova o próprio certificado; em produção, outra pessoa aprova" />).
          Em produção, o banco recusa.
        </p>
      ) : (
        <p className="sub">
          Aprovado não volta atrás: uma correção exige certificado novo, com nova
          numeração. E quem calibrou não pode aprovar o próprio trabalho — o banco
          recusa.
        </p>
      )}
      {precisaData ? (
        <Campo
          rotulo="Próxima calibração interna" type="date" required
          value={proxima} onChange={(e) => setProxima(e.target.value)}
          min={certificado.data_afericao.slice(0, 10)}
          dica="Nenhum objeto deste certificado tem periodicidade de verificação
            cadastrada. Sem vencimento, a constante valeria para sempre."
          erro={proxima && dataInvalida ? 'depois da data da calibração interna' : undefined}
        />
      ) : !certificado.proxima_afericao && certificado.proxima_sugerida ? (
        <p className="sub">
          Próxima calibração interna: <b>{fData(certificado.proxima_sugerida)}</b>, pela
          periodicidade de verificação dos objetos.
        </p>
      ) : null}
      {Number(certificado.objetos_nao_conformes) > 0 && (
        <Aviso tipo="atencao" titulo="Há objeto não conforme">
          {certificado.objetos_nao_conformes} objeto(s) fora de tolerância não
          publicam valor, salvo uso autorizado com justificativa.
        </Aviso>
      )}
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="acoes">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button
          type="button" className="botao" disabled={indo || dataInvalida}
          onClick={enviar}
        >
          {indo ? 'Aprovando…' : 'Confirmo — aprovar e publicar'}
        </button>
      </div>
    </Dialogo>
  );
}

function FormAutorizarUso({
  objeto, aoFechar,
}: { objeto: Objeto; aoFechar(): void }) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);

  const enviar = async () => {
    setIndo(true);
    try {
      await api(`/verificacoes/objetos/${objeto.id}/autorizar-uso`,
        { corpo: { justificativa: texto } });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };

  return (
    <Dialogo aberto titulo="Autorizar o uso de um objeto não conforme" aoFechar={aoFechar}>
      <p>
        {objeto.codigo_patrimonial} · {objeto.equipamento} está fora de
        tolerância. Autorizar o uso registra <b>quem decidiu, quando e por
        quê</b> — e passa a publicar as constantes dele.
      </p>
      <Area
        rotulo="Justificativa"
        rows={4}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        dica="Quem ler o laudo daqui a três anos precisa entender a decisão."
      />
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="acoes">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button
          type="button" className="botao"
          disabled={indo || texto.trim().length < 20}
          onClick={enviar}
        >
          {indo ? 'Registrando…' : 'Registrar a autorização'}
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Abrir a campanha — o cabeçalho do papel.
 *
 * O que se digita aqui é o que uma pessoa realmente digita: número,
 * título, data, procedimento. Os 28 cilindros medidos vêm depois, e não
 * por formulário — ver a nota em `SEM_TELA_AINDA` no teste 26.
 */
function FormCertificado({ aoFechar }: { aoFechar(): void }) {
  const [v, setV] = useState({
    codigo: '',
    titulo: '',
    categoria: '',
    dataAfericao: '',
    procedimento: '',
    condicoesAmbientais: '21 ± 1 °C e 60 ± 5 % ur',
    proximaAfericao: '',
  });
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  const enviar = async () => {
    setIndo(true);
    try {
      await api('/verificacoes/certificados', {
        corpo: {
          codigo: v.codigo,
          titulo: v.titulo,
          categoria: v.categoria,
          dataAfericao: v.dataAfericao,
          procedimento: v.procedimento,
          condicoesAmbientais: v.condicoesAmbientais || undefined,
          proximaAfericao: v.proximaAfericao || undefined,
        },
      });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };

  const pronto = v.codigo && v.titulo && v.categoria
    && v.dataAfericao && v.procedimento.length >= 3;

  return (
    <Dialogo aberto titulo="Abrir certificado de calibração interna" aoFechar={aoFechar}>
      <p className="sub">
        Um certificado cobre a campanha inteira — uma data, um responsável,
        um jogo de padrões. Emitir um certificado por cilindro seria
        inventar 28 eventos que não aconteceram.
      </p>
      <div className="linha">
        <Campo rotulo="Número" required maxLength={40}
          placeholder="2604" {...campo('codigo')} />
        <Campo rotulo="Data da calibração interna" type="date" required
          {...campo('dataAfericao')} />
      </div>
      <Campo rotulo="Título" required maxLength={200}
        placeholder="Cilindros metálicos grandes (ISC)" {...campo('titulo')} />
      <Campo rotulo="Categoria do que foi calibrado" required maxLength={40}
        placeholder="cilindro_cbr"
        dica="A mesma categoria do cadastro de equipamentos."
        {...campo('categoria')} />
      <Campo rotulo="Procedimento" required maxLength={400}
        placeholder="NBR 7182 (ABNT) e DNER-ME 162"
        dica="A norma e o procedimento interno que mandam medir assim."
        {...campo('procedimento')} />
      <Campo rotulo="Condições ambientais" maxLength={200}
        {...campo('condicoesAmbientais')} />
      <Campo rotulo="Próxima calibração interna" type="date" {...campo('proximaAfericao')} />
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="acoes">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao" disabled={indo || !pronto}
          onClick={enviar}>
          {indo ? 'Abrindo…' : 'Abrir o certificado'}
        </button>
      </div>
    </Dialogo>
  );
}

/** Um padrão que sustenta a campanha, com o número do certificado dele. */
function FormPadrao({
  certificado, proximaOrdem, aoFechar,
}: { certificado: Certificado; proximaOrdem: number; aoFechar(): void }) {
  const [v, setV] = useState({ descricao: '', numeroCertificado: '' });
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);
  const campo = (k: keyof typeof v) => ({
    value: v[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setV({ ...v, [k]: e.target.value }),
  });

  const enviar = async () => {
    setIndo(true);
    try {
      await api(`/verificacoes/certificados/${certificado.id}/padroes`, {
        corpo: {
          ordem: proximaOrdem,
          descricao: v.descricao,
          numeroCertificado: v.numeroCertificado || undefined,
        },
      });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };

  return (
    <Dialogo aberto titulo="Acrescentar padrão" aoFechar={aoFechar}>
      <p className="sub">
        O volume do cilindro vale porque o paquímetro que o mediu tinha
        certificado válido naquele dia. Sem esta linha, «calibrado
        internamente» é só uma frase.
      </p>
      <Campo rotulo="Padrão" required maxLength={300}
        placeholder="Paquímetro PAQ-1045" {...campo('descricao')} />
      <Campo rotulo="Certificado do padrão" maxLength={120}
        placeholder="260421-55" {...campo('numeroCertificado')} />
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="acoes">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao"
          disabled={indo || v.descricao.trim().length < 3} onClick={enviar}>
          {indo ? 'Gravando…' : 'Acrescentar'}
        </button>
      </div>
    </Dialogo>
  );
}
